import { Flex, Tag, Text } from '@spotify-internal/encore-web';
import { getTypeOrNull } from '@spotify/confidence-graphql';
import { useFlag } from '@spotify/plugin-confidence-flags-react';

import {
  PreviewShell,
  RuleItem,
  SummarySection,
  TypeStatusRow,
  VariantList,
  buildVariantUsage,
  resolveDisplayName,
} from './components';

interface FlagArtifactPreviewProps {
  resourceName: string;
  label: string;
}

export const FlagArtifactPreview = ({
  resourceName,
  label,
}: FlagArtifactPreviewProps) => {
  const { flag, isLoading, error } = useFlag({ name: resourceName });

  const previewError = error ?? (!isLoading && !flag ? 'not-found' : undefined);
  const variantUsage = buildVariantUsage(flag?.rules ?? []);

  return (
    <PreviewShell loading={isLoading} error={previewError}>
      {flag && (
        <>
          <TypeStatusRow
            typeLabel={label}
            state={flag.state}
            demoLabels={flag.labels}
          />

          {flag.description && (
            <Text variant="bodySmall" semanticColor="textSubdued">
              {flag.description}
            </Text>
          )}

          <SummarySection title="Clients">
            {flag.clients.length === 0 ? (
              <Text variant="bodySmall" semanticColor="textSubdued">
                No clients.
              </Text>
            ) : (
              <Flex wrap="wrap" gap="tighter-2" aria-label="Clients">
                {flag.clients
                  .map(client => getTypeOrNull(client))
                  .filter((c): c is NonNullable<typeof c> => Boolean(c))
                  .map(client => (
                    <Tag key={client.name}>{resolveDisplayName(client)}</Tag>
                  ))}
              </Flex>
            )}
          </SummarySection>

          {flag.variants.length > 0 && (
            <SummarySection title="Variants">
              <VariantList
                variants={flag.variants}
                variantUsage={variantUsage}
              />
            </SummarySection>
          )}

          <SummarySection title="Rules">
            {flag.rules.length === 0 ? (
              <Text variant="bodySmall" semanticColor="textSubdued">
                No rules.
              </Text>
            ) : (
              <Flex direction="column" gap="tighter">
                {flag.rules.map(rule => (
                  <RuleItem key={rule.name} rule={rule} />
                ))}
              </Flex>
            )}
          </SummarySection>
        </>
      )}
    </PreviewShell>
  );
};
